import { Link } from '@tanstack/react-router';
import { PackageOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Button } from '@nucleus-commerce/ui/components/button';

import { useStoreDetail } from '@/features/stores';
import { ReadOnlyBanner } from '@/shared/store/read-only-banner';
import { useStore } from '@/shared/store/store-context';
import { useStoreEditable } from '@/shared/store/use-store-editable';

import { ReturnsSettingsCard } from '../components/returns-settings-card';
import { returnListSearchSchema } from '../schemas/return-list-search';

function ReturnsSettings() {
  const { t } = useTranslation('returns');
  const store = useStore();
  const { reason } = useStoreEditable({ permission: 'settings.write' });
  // Prefetched by the route loader.
  const { data: detail } = useStoreDetail(store.id);

  return (
    <PageContent>
      <PageHeader
        title={t('settings.pageTitle')}
        description={t('settings.pageDescription', { store: store.name })}
        backTo={{ to: '/org/stores/$storeId', params: { storeId: store.id } }}
        backLabel={t('settings.back')}
        actions={
          <Button asChild variant="outline">
            <Link
              to="/s/$storeId/returns"
              params={{ storeId: store.id }}
              // indexSearchSchema types every param as required input; an empty parse is the whole list.
              search={returnListSearchSchema.parse({})}
            >
              <PackageOpen aria-hidden />
              {t('settings.viewReturns')}
            </Link>
          </Button>
        }
      />
      <ReadOnlyBanner reason={reason} />
      <div className="max-w-2xl">
        {detail ? <ReturnsSettingsCard key={detail.updatedAt} store={detail} /> : null}
      </div>
    </PageContent>
  );
}

export function ReturnsSettingsPage() {
  return (
    <FeatureErrorBoundary name="returns.settings">
      <ReturnsSettings />
    </FeatureErrorBoundary>
  );
}
